import React, { useContext, useState, useEffect, useReducer, useMemo } from 'react';
import { find, map, keyBy, filter, orderBy, includes, some } from 'lodash';
import { Link, useHistory, useParams } from 'react-router-dom';
import ConfirmBox from '../../../../components/common/ConfirmBox';
import { StateContext } from '../../../../App';
import { builderPermissions, routes } from '../../../data/constants';
import { hasPermission } from '../../../data/session';
import { v4 } from 'uuid';
import { advertisementDeleteHandler, advertisementsSetHandler } from '../actions';
import { reducer, initialState } from '../reducer';
import cogoToast from 'cogo-toast';
import LoadingSkeleton from './LoadingSkeleton';
import AdvertisementRow from './AdvertisementRow';
import Search from '../../../../components/common/Search';
import { getCampaignStatus } from '../../../../utils';
import GroupModal from '../../groups/components/GroupModal';
import { saveAdvertisementGroup, SINGLE_DISPLAY_MODE, multipleDisplayModes, displayModeSummary } from '../../groups/components/GroupMenu';
import Dropdown from '../../../../components/common/Dropdown';
import { AdvertisementQuery, AdSaveMutation } from '../../detail/components/Detail';

/* global __COMPONENTS_BUNDLE_SOURCE__ */
const componentsBundle = typeof __COMPONENTS_BUNDLE_SOURCE__ === 'string' ? __COMPONENTS_BUNDLE_SOURCE__ : '';
// Ignore case and treat spaces/underscores as hyphens. Only used to warn that a group named "Vehicle Detail Top"
// is almost, but not exactly, the "vehicle-detail-top" slug hardcoded in dealer-website-components.
const normalizeGroupName = (name) =>
  String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

// Collect the group ids dealer-website-components actually passes to <AdvertiseComponent groupId=... /> (or groupName=... in e.g. lma).
// Scoped to the `groupId:` / `groupName:` prop (not any quoted string) so CSS classes etc. can't cause false positives.
// Computed once at module load — the bundle source is a build-time constant.
const groupIdsInComponents = (() => {
  const ids = new Set();
  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const ternaryBranches = /\?\s*(["'])([^"']+)\1\s*:\s*(["'])([^"']+)\3/;
  // `{groupId:"slug"` / `,groupId: 'slug'` (inline literal), `groupId: cond ? "a" : "b"` (inline ternary) or `groupId:ident` (a local variable).
  const propRe = /[{,]\s*group(?:Id|Name)\s*:\s*([^,{}]+)/g;
  const identifiers = new Set();
  let m;
  while ((m = propRe.exec(componentsBundle)) !== null) {
    const value = m[1].trim();
    const literal = value.match(/^(["'])([^"']+)\1$/);
    const ternary = value.match(ternaryBranches);
    if (literal) ids.add(literal[2]);
    else if (ternary) [ternary[2], ternary[4]].forEach((branch) => ids.add(branch));
    // Skip member expressions like `t.groupId` (config-driven pass-through, not statically resolvable).
    else if (/^[A-Za-z_$][\w$]*$/.test(value)) identifiers.add(value);
  }
  // Resolve the campaign ternary pattern `ident = cond ? "slug-campaign" : "slug"` for each local variable.
  // Only hyphenated branches are kept, to avoid picking up reused vars assigned CSS values (e.g. `x=u?"block":"hidden"`).
  identifiers.forEach((id) => {
    const ternaryRe = new RegExp(escapeRegExp(id) + '\\s*=[^;{}]{0,80}?' + ternaryBranches.source, 'g');
    let t;
    while ((t = ternaryRe.exec(componentsBundle)) !== null) {
      [t[2], t[4]].forEach((branch) => {
        if (branch.includes('-')) ids.add(branch);
      });
    }
  });
  return ids;
})();
const normalizedGroupIdsInComponents = new Set([...groupIdsInComponents].map(normalizeGroupName));

// Recursively collect every `groupId` string referenced anywhere in the CMS page config
// (context.pages[].blocks[].content[].fields[].value.groupId, at any nesting depth).
// These come from the backend; collected raw so we can match them against both group id and name.
const collectGroupIdRefs = (node, acc) => {
  if (!node || typeof node !== 'object') return acc;
  if (Array.isArray(node)) {
    node.forEach((item) => collectGroupIdRefs(item, acc));
    return acc;
  }
  Object.keys(node).forEach((key) => {
    const val = node[key];
    if (key === 'groupId' && typeof val === 'string' && val.trim() !== '') {
      acc.add(val);
    } else if (val && typeof val === 'object') {
      collectGroupIdRefs(val, acc);
    }
  });
  return acc;
};

export const AdsQuery = `query Advertisements{
  advertisements {
    id
    description
    content{
      culture
    }
    isDraft
    disabled
    groups
    filters
    campaignId
    isPrimary
    locked
    source
  }
  advertisementGroups {
    id
    name
    isDraft
    disabled
    displayMode
    locked
    source
  }
  campaigns {
    id
    name
    start
    stop
  }
}`;

export const AdGroupDeleteMutation = `mutation AdvertisementGroupDelete($data: AdvertisementGroupDeleteInputType!) {
  advertisementGroupDelete(data: $data) {
    ok
  }
}`;

export const AdDeleteMutation = `mutation AdvertisementDelete($data: AdvertisementDeleteInputType!) {
  advertisementDelete(data: $data) {
    ok
  }
}`;

export const AdvertisementsSyncPushMutation = `mutation AdvertisementsSyncPush {
  advertisementsSyncPush {
    results {
      url
      ok
      error
    }
  }
}`;

const NO_GROUP = 'no-group';

const usageLabels = { used: 'On the website', nameMismatch: 'Name does not match the website', unused: 'Not on the website yet' };
const usageDotClasses = { used: 'builder-bg-green-500', nameMismatch: 'builder-bg-yellow-500', unused: 'builder-bg-gray-400' };
const usagePillClasses = { used: 'builder-bg-green-100 builder-text-green-800', nameMismatch: 'builder-bg-yellow-100 builder-text-yellow-800', unused: 'builder-bg-gray-100 builder-text-gray-700' };

const displayOptions = [
  { showMultiple: false, title: 'One advertisement', text: 'The primary one when it matches, otherwise the first one that matches.' },
  { showMultiple: true, title: 'All advertisements', text: 'Every advertisement that matches, the primary one first.' }
];

const countLabel = (count) => `${count} ad${count === 1 ? '' : 's'}`;
const descriptionSortValue = (ad) => (ad.description || '').toLowerCase();
const errorMessage = (result) => (result && result.errors && result.errors.length > 0 ? result.errors[0].message : 'Something went wrong');

const postGraphql = (query, variables) =>
  fetch('/graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ query, variables })
  }).then((resp) => resp.json());

const saveAdvertisementPrimary = (id, isPrimary) =>
  postGraphql(AdvertisementQuery, { data: { id } }).then((result) => {
    const ad = result?.data?.advertisements?.[0];
    if (!ad) return result;

    return postGraphql(AdSaveMutation, {
      data: {
        id: ad.id,
        description: ad.description,
        content: [],
        groups: ad.groups,
        filters: ad.filters,
        disabled: ad.disabled,
        isDraft: ad.isDraft,
        campaignId: ad.campaignId,
        isPrimary
      }
    });
  });

const Overview = ({ context }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const contextState = useContext(StateContext);
  const history = useHistory();
  const { groupId: groupIdFromUrl } = useParams();

  const [confirmDelete, setConfirmDelete] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(undefined);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [confirmSync, setConfirmSync] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(undefined);
  const canSyncAdvertisements = hasPermission(contextState.session, builderPermissions.ADVERTISEMENTS_SYNC) && !!contextState?.configuration?.builder?.advertisements_sync;
  const syncTargets = contextState?.configuration?.cms?.integrations?.advertisements_sync?.targets || [];
  const currentWebsite = find(contextState?.websites, (w) => w.id === contextState.currentWebsite);
  const isCurrentSiteProduction = currentWebsite ? currentWebsite.isProduction : false;

  const advertisements = state.advertisements;
  const advertisementGroups = state.advertisementGroups;
  const groupsById = keyBy(advertisementGroups, 'id');
  const campaignsById = keyBy(state.campaigns, 'id');
  const languages = contextState?.configuration?.website?.languages;

  // The website only finds a group by its exact name (or id); a name that differs only in case or spacing is flagged.
  const groupUsage = useMemo(() => {
    const cmsRefs = collectGroupIdRefs(context?.pages, new Set());
    const normalizedCmsRefs = new Set([...cmsRefs].map(normalizeGroupName));

    const usage = {};
    (advertisementGroups || []).forEach((group) => {
      const normalizedName = normalizeGroupName(group.name);
      if (groupIdsInComponents.has(group.name) || cmsRefs.has(group.name) || cmsRefs.has(group.id)) {
        usage[group.id] = 'used';
      } else if (normalizedGroupIdsInComponents.has(normalizedName) || normalizedCmsRefs.has(normalizedName)) {
        usage[group.id] = 'nameMismatch';
      } else {
        usage[group.id] = 'unused';
      }
    });
    return usage;
  }, [advertisementGroups, context?.pages]);

  // An advertisement is "active" if at least one of its groups is used on the website
  // and its campaign, if it has one, is running today.
  const isAdvertisementActive = (advertisement) => {
    const campaignStatus = getCampaignStatus(campaignsById[advertisement?.campaignId]);
    if (campaignStatus === 'expired' || campaignStatus === 'upcoming') return false;

    return some(advertisement?.groups, (groupId) => groupUsage[groupId] === 'used');
  };

  const getStatusTitle = (isActive, campaign) => {
    if (isActive) return 'At least one group is used on the website';
    const campaignStatus = getCampaignStatus(campaign);
    if (campaignStatus === 'expired') return 'The campaign has ended — this advertisement is no longer displayed';
    if (campaignStatus === 'upcoming') return 'The campaign has not started yet — this advertisement is not displayed yet';
    return 'None of its groups is used on the website — this advertisement will not be displayed';
  };

  // Only edit/delete drafts in draft environment (production posts are not editable in draft).
  const canEditAdvertisement = (advertisement) => !advertisement.locked && !(advertisement.isDraft && !isCurrentSiteProduction);

  const advertisementsOfGroup = (groupId) => filter(advertisements, (ad) => includes(ad.groups, groupId));
  const advertisementsWithoutGroup = filter(advertisements, (ad) => !some(ad.groups, (groupId) => groupsById[groupId]));
  const groupsByName = orderBy(advertisementGroups, [(group) => (group.name || '').toLowerCase()]);

  const searchTerm = search.trim().toLowerCase();
  const matchesSearch = (group) => searchTerm === '' || (group.name || '').toLowerCase().includes(searchTerm) || some(advertisementsOfGroup(group.id), (ad) => descriptionSortValue(ad).includes(searchTerm));
  const visibleGroups = filter(groupsByName, matchesSearch);
  const groupsWithAds = filter(visibleGroups, (group) => advertisementsOfGroup(group.id).length > 0);
  const groupsWithoutAds = filter(visibleGroups, (group) => advertisementsOfGroup(group.id).length === 0);

  const defaultKey = find(groupsByName, (group) => advertisementsOfGroup(group.id).length > 0)?.id || groupsByName[0]?.id || NO_GROUP;
  const selectedKey = groupIdFromUrl === NO_GROUP || groupsById[groupIdFromUrl] ? groupIdFromUrl : defaultKey;
  const selectedGroup = groupsById[selectedKey];

  const panelAdvertisements = orderBy(selectedGroup ? advertisementsOfGroup(selectedGroup.id) : advertisementsWithoutGroup, [(ad) => (selectedGroup && ad.isPrimary ? 0 : 1), descriptionSortValue]);

  // Select-all applies to the advertisements of the selected group that can be deleted.
  const selectableIds = filter(panelAdvertisements, (ad) => canEditAdvertisement(ad)).map((ad) => ad.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const someSelected = selectableIds.some((id) => selectedIds.has(id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) selectableIds.forEach((id) => next.delete(id));
      else selectableIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const fetchAds = () => {
    postGraphql(AdsQuery)
      .then((result) => {
        advertisementsSetHandler(dispatch, result.data.advertisements, result.data.advertisementGroups, result.data.campaigns);
      })
      .catch((err) => {});
  };

  const handleDelete = () => {
    const id = confirmDelete;

    postGraphql(AdDeleteMutation, { data: { id: id } })
      .then((result) => {
        if (result?.data?.advertisementDelete?.ok) {
          advertisementDeleteHandler(dispatch, id);
          cogoToast.success('Advertisement deleted');
        } else {
          cogoToast.error(errorMessage(result));
        }
        setConfirmDelete('');
      })
      .catch((err) => {});
  };

  const handleBulkDelete = () => {
    const ids = [...selectedIds];

    Promise.all(
      ids.map((id) =>
        postGraphql(AdDeleteMutation, { data: { id: id } })
          .then((result) => ({ id, ok: !!result?.data?.advertisementDelete?.ok }))
          .catch(() => ({ id, ok: false }))
      )
    ).then((results) => {
      const deleted = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);

      deleted.forEach((r) => advertisementDeleteHandler(dispatch, r.id));

      if (deleted.length > 0) {
        cogoToast.success(`${deleted.length} advertisement${deleted.length > 1 ? 's' : ''} deleted`);
      }
      if (failed.length > 0) {
        cogoToast.error(`${failed.length} advertisement${failed.length > 1 ? 's' : ''} could not be deleted`);
      }

      setSelectedIds(new Set());
      setConfirmBulkDelete(false);
    });
  };

  const openGroupModal = (group) => {
    setEditGroup(group);
    setIsGroupModalOpen(true);
  };

  const handleDeleteGroup = () => {
    const group = confirmDeleteGroup;

    postGraphql(AdGroupDeleteMutation, { data: { id: group.id } })
      .then((result) => {
        if (result?.data?.advertisementGroupDelete?.ok) {
          cogoToast.success('Advertisement group deleted');
          history.push(routes.ADVERTISEMENTS);
          fetchAds();
        } else {
          cogoToast.error(errorMessage(result));
        }
        setConfirmDeleteGroup(undefined);
      })
      .catch((err) => {});
  };

  const closeGroupModal = () => {
    setIsGroupModalOpen(false);
    setEditGroup(undefined);
  };

  const handleSaveGroup = (group) => {
    const isNew = !editGroup;

    saveAdvertisementGroup(group)
      .then((result) => {
        if (result?.data?.advertisementGroupUpdate?.ok) {
          cogoToast.success('Advertisement Group Saved');
          closeGroupModal();
          fetchAds();
          if (isNew) history.push(`${routes.ADVERTISEMENTS}/${group.id}`);
        } else {
          cogoToast.error(errorMessage(result));
        }
      })
      .catch((err) => {});
  };

  const handleDisplayModeChange = (group, displayMode) => {
    if (saving || displayMode === group.displayMode) return;
    setSaving(true);
    saveAdvertisementGroup({ ...group, displayMode })
      .then((result) => {
        if (result?.data?.advertisementGroupUpdate?.ok) {
          cogoToast.success(`This group now ${displayModeSummary(displayMode)}`);
          fetchAds();
        } else {
          cogoToast.error(errorMessage(result));
        }
      })
      .catch((err) => {})
      .finally(() => setSaving(false));
  };

  const handleTogglePrimary = (advertisement) => {
    const makePrimary = !advertisement.isPrimary;
    const otherPrimaries = makePrimary ? filter(panelAdvertisements, (ad) => ad.isPrimary && ad.id !== advertisement.id) : [];

    setSaving(true);
    Promise.all(map(otherPrimaries, (ad) => saveAdvertisementPrimary(ad.id, false)))
      .then((results) => find(results, (result) => !result?.data?.advertisementUpdate?.ok) || saveAdvertisementPrimary(advertisement.id, makePrimary))
      .then((result) => {
        if (result?.data?.advertisementUpdate?.ok) {
          cogoToast.success(makePrimary ? 'Primary advertisement changed' : 'Primary advertisement removed');
        } else {
          cogoToast.error(errorMessage(result));
        }
        fetchAds();
      })
      .catch((err) => {})
      .finally(() => setSaving(false));
  };

  const handleSync = () => {
    setConfirmSync(false);
    setSyncing(true);
    postGraphql(AdvertisementsSyncPushMutation)
      .then((result) => {
        const results = result?.data?.advertisementsSyncPush?.results;
        if (results) {
          setSyncResults(results);
        } else {
          cogoToast.error(errorMessage(result));
        }
      })
      .catch((err) => cogoToast.error('Something went wrong'))
      .finally(() => setSyncing(false));
  };

  const onEditAdvertisement = (id) => {
    history.push(`${routes.ADVERTISEMENT_DETAIL}/${id}`, {
      language: context?.culture || 'nl',
      fromGroupId: selectedKey
    });
  };

  const onCreateAdvertisement = () => {
    history.push(`${routes.ADVERTISEMENT_DETAIL}/${v4()}`, {
      language: 'nl',
      fromGroupId: selectedKey,
      groupId: selectedGroup ? selectedGroup.id : undefined
    });
  };

  useEffect(() => {
    fetchAds();
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedKey]);

  if (state.advertisements === undefined || state.advertisementsLoading) {
    return <LoadingSkeleton />;
  }

  const renderGroupList = (title, groups, dataTestId) => (
    <div className='builder-pb-2' data-testid={dataTestId}>
      <div className='builder-px-4 builder-pt-3 builder-pb-1 builder-text-sm builder-font-bold builder-uppercase builder-tracking-wider builder-text-gray-600'>
        {title} ({groups.length})
      </div>
      {groups.length === 0 && <div className='builder-px-4 builder-py-2 builder-text-sm builder-text-gray-500'>None</div>}
      {map(groups, (group) => {
        const selected = group.id === selectedKey;
        return (
          <Link
            key={group.id}
            to={`${routes.ADVERTISEMENTS}/${group.id}`}
            aria-current={selected ? 'page' : undefined}
            className={`builder-flex builder-items-center builder-justify-between builder-gap-2 builder-px-4 builder-py-2 builder-text-sm builder-border-l-4 ${
              selected ? 'builder-bg-gray-100 builder-border-primary builder-font-bold' : 'builder-border-transparent hover:builder-bg-gray-50'
            }`}
          >
            <span className='builder-flex builder-items-center builder-gap-2 builder-min-w-0'>
              <span className={`builder-flex-shrink-0 builder-w-2 builder-h-2 builder-rounded-full ${usageDotClasses[groupUsage[group.id]]}`} title={usageLabels[groupUsage[group.id]]} />
              <span className='builder-truncate builder-text-black'>{group.name}</span>
              {group.locked && <i className='fal fa-lock builder-text-gray-600' style={{ fontSize: 11 }} title={`Synced from ${group.source}`} />}
            </span>
            <span className='builder-text-gray-600'>{advertisementsOfGroup(group.id).length}</span>
          </Link>
        );
      })}
    </div>
  );

  const renderAdvertisementList = (showPrimary, emptyText) => (
    <div className='builder-bg-white builder-border builder-border-gray-200 builder-shadow-sm'>
      <div className='builder-flex builder-flex-wrap builder-items-center builder-justify-between builder-gap-3 builder-px-5 builder-py-3'>
        <div className='builder-flex builder-items-center builder-gap-3'>
          {panelAdvertisements.length > 1 && (
            <input
              type='checkbox'
              className='builder-cursor-pointer'
              checked={allSelected}
              disabled={selectableIds.length === 0}
              ref={(el) => {
                if (el) el.indeterminate = !allSelected && someSelected;
              }}
              onChange={toggleSelectAll}
              aria-label='Select all advertisements'
              data-testid='advertisements-select-all'
            />
          )}
          <h3 className='builder-font-bold builder-text-black'>{countLabel(panelAdvertisements.length)}</h3>
        </div>
        <div className='builder-flex builder-items-center builder-gap-3'>
          {selectedIds.size > 0 && (
            <button
              type='button'
              onClick={() => setConfirmBulkDelete(true)}
              data-testid='advertisements-bulk-delete-button'
              className='builder-px-3 builder-py-1.5 builder-bg-red-600 builder-text-white builder-rounded builder-font-semibold builder-whitespace-nowrap'
            >
              <i className='fal fa-trash-alt builder-mr-2' />
              Delete selected ({selectedIds.size})
            </button>
          )}
          {showPrimary && (
            <button type='button' onClick={onCreateAdvertisement} className='builder-flex builder-items-center builder-text-primary builder-font-semibold builder-whitespace-nowrap'>
              <i className='fal fa-plus builder-mr-2' />
              Add advertisement
            </button>
          )}
        </div>
      </div>
      {panelAdvertisements.length === 0 ? (
        <div className='builder-px-5 builder-py-6 builder-border-t builder-border-gray-100 builder-text-gray-600'>{emptyText}</div>
      ) : (
        <ul>
          {map(panelAdvertisements, (advertisement) => {
            const isActive = isAdvertisementActive(advertisement);
            const campaign = campaignsById[advertisement.campaignId];
            return (
              <AdvertisementRow
                key={advertisement.id}
                advertisement={advertisement}
                campaign={campaign}
                languages={languages}
                isActive={isActive}
                statusTitle={getStatusTitle(isActive, campaign)}
                canEdit={canEditAdvertisement(advertisement)}
                selected={selectedIds.has(advertisement.id)}
                onToggleSelect={() => toggleSelect(advertisement.id)}
                onEdit={() => onEditAdvertisement(advertisement.id)}
                onDelete={() => setConfirmDelete(advertisement.id)}
                showPrimary={showPrimary && (panelAdvertisements.length > 1 || advertisement.isPrimary)}
                onTogglePrimary={() => handleTogglePrimary(advertisement)}
                primarySaving={saving}
              />
            );
          })}
        </ul>
      )}
    </div>
  );

  const renderGroupPanel = (group) => {
    const usage = groupUsage[group.id];
    const advertisementCount = advertisementsOfGroup(group.id).length;
    const hasAdvertisements = advertisementCount > 0;

    return (
      <React.Fragment>
        <div className='builder-flex builder-flex-col builder-gap-4 builder-p-5 builder-bg-white builder-border builder-border-gray-200 builder-shadow-sm'>
          <div className='builder-flex builder-flex-wrap builder-items-center builder-justify-between builder-gap-3'>
            <div className='builder-flex builder-flex-wrap builder-items-center builder-gap-3'>
              <h2 className='builder-font-bold builder-text-black' style={{ fontSize: 20 }}>
                {group.name}
              </h2>
              <span className={`builder-inline-flex builder-items-center builder-px-2.5 builder-py-0.5 builder-rounded-full builder-text-sm builder-font-semibold ${usagePillClasses[usage]}`}>
                <span className={`builder-w-2 builder-h-2 builder-rounded-full builder-mr-1.5 ${usageDotClasses[usage]}`} />
                {usageLabels[usage]}
              </span>
            </div>
            {group.locked && (
              <span className='builder-flex builder-items-center builder-font-bold' title='Synced groups can only be changed in the source builder'>
                <i className='fal fa-lock builder-mr-2' style={{ fontSize: 12 }} />
                Synced from {group.source}
              </span>
            )}
            {!group.locked && (
              <div className='builder-flex builder-items-center builder-gap-2'>
                <button type='button' onClick={() => openGroupModal(group)} className='builder-flex builder-items-center builder-px-3 builder-py-1.5 builder-border builder-border-gray-300 builder-bg-white' data-testid='advertisement-group-rename'>
                  <i className='fal fa-pencil-alt builder-mr-2' style={{ fontSize: 12 }} />
                  Rename
                </button>
                <span title={hasAdvertisements ? 'A group can only be deleted when it has no advertisements. Delete them or move them to another group first.' : undefined}>
                  <button
                    type='button'
                    onClick={() => setConfirmDeleteGroup(group)}
                    disabled={hasAdvertisements}
                    className={`builder-flex builder-items-center builder-px-3 builder-py-1.5 builder-border builder-bg-white ${
                      hasAdvertisements ? 'builder-border-gray-200 builder-text-gray-400 builder-cursor-not-allowed' : 'builder-border-gray-300 builder-text-red-700'
                    }`}
                    data-testid='advertisement-group-delete'
                  >
                    <i className='fal fa-trash-alt builder-mr-2' style={{ fontSize: 12 }} />
                    Delete
                  </button>
                </span>
              </div>
            )}
          </div>

          {usage === 'nameMismatch' && (
            <div className='builder-p-3 builder-border builder-border-yellow-300 builder-bg-yellow-50 builder-text-sm builder-text-yellow-900'>
              The website looks for a group named <strong>{normalizeGroupName(group.name)}</strong>, but this group is called <strong>&quot;{group.name}&quot;</strong>. Check spaces and capitals: until the names match exactly, its advertisements are
              not shown.
            </div>
          )}
          {usage === 'unused' && <div className='builder-p-3 builder-border builder-border-gray-200 builder-bg-gray-50 builder-text-sm builder-text-gray-700'>This group is not placed on any page yet, so its advertisements are not shown.</div>}

          {advertisementCount > 1 ? (
            <fieldset disabled={saving || group.locked}>
              <legend className='builder-mb-2 builder-text-sm builder-font-bold builder-text-black'>What do visitors see?</legend>
              <div className='builder-flex builder-flex-wrap builder-gap-3'>
                {map(displayOptions, (option) => {
                  const checked = (group.displayMode !== SINGLE_DISPLAY_MODE) === option.showMultiple;
                  return (
                    <div
                      key={option.title}
                      className={`builder-flex builder-flex-col builder-flex-1 builder-gap-2 builder-p-3 ${checked ? 'builder-border-2 builder-border-primary' : 'builder-border builder-border-gray-300'}`}
                      style={{ minWidth: 240 }}
                    >
                      <label className='builder-flex builder-items-start builder-gap-2 builder-cursor-pointer'>
                        <input
                          type='radio'
                          name={`show-multiple-${group.id}`}
                          checked={checked}
                          onChange={() => handleDisplayModeChange(group, option.showMultiple ? multipleDisplayModes[0].key : SINGLE_DISPLAY_MODE)}
                          className='builder-mt-1'
                          data-testid={`advertisement-group-show-${option.showMultiple ? 'all' : 'one'}`}
                        />
                        <span className='builder-flex builder-flex-col'>
                          <span className='builder-font-semibold builder-text-black'>{option.title}</span>
                          <span className='builder-text-sm builder-text-gray-600'>{option.text}</span>
                        </span>
                      </label>
                      {checked && option.showMultiple && (
                        <div style={{ paddingLeft: 21 }} data-testid='advertisement-group-display-mode'>
                          <Dropdown
                            options={multipleDisplayModes}
                            value={group.displayMode}
                            maxWidth='100%'
                            placeHolder='Choose how they are shown'
                            borderClassName='builder-border builder-border-gray-300 builder-rounded-md'
                            selectClassName='builder-py-2 builder-rounded-md'
                            renderItem={(item) => (
                              <div className='builder-p-2 builder-text-sm' onClick={() => handleDisplayModeChange(group, item.key)}>
                                {item.value}
                              </div>
                            )}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ) : (
            <p className='builder-text-sm builder-text-gray-600'>Once this group has more than one advertisement, you can choose how they are shown.</p>
          )}
        </div>

        {renderAdvertisementList(true, 'This group has no advertisements yet.')}
      </React.Fragment>
    );
  };

  const renderNoGroupPanel = () => (
    <React.Fragment>
      <div className='builder-flex builder-flex-col builder-gap-1 builder-p-5 builder-bg-white builder-border builder-border-gray-200 builder-shadow-sm'>
        <h2 className='builder-font-bold builder-text-black' style={{ fontSize: 20 }}>
          Ads without a group
        </h2>
        <p className='builder-text-sm builder-text-gray-600'>These advertisements are not shown anywhere. Add them to a group to show them, or delete the ones you no longer need.</p>
      </div>
      {renderAdvertisementList(false, 'Every advertisement is in a group.')}
    </React.Fragment>
  );

  return (
    <div className='builder-flex builder-flex-col builder-p-5 builder-w-full'>
      {confirmDelete !== '' && <ConfirmBox title='Delete advertisement' text='Are you sure you want to delete this advertisement?' type='DANGER' onCancel={() => setConfirmDelete('')} onConfirm={handleDelete} />}
      {confirmBulkDelete && (
        <ConfirmBox
          title='Delete advertisements'
          text={`Are you sure you want to delete ${selectedIds.size} selected advertisement${selectedIds.size > 1 ? 's' : ''}?`}
          type='DANGER'
          onCancel={() => setConfirmBulkDelete(false)}
          onConfirm={handleBulkDelete}
        />
      )}
      {confirmSync && (
        <ConfirmBox
          title='Sync advertisements'
          text={`This overwrites the synced advertisements, groups and campaigns on ${syncTargets.length} builder${syncTargets.length === 1 ? '' : 's'}. Are you sure?`}
          type='WARNING'
          onCancel={() => setConfirmSync(false)}
          onConfirm={handleSync}
        />
      )}
      {confirmDeleteGroup && (
        <ConfirmBox
          title='Delete advertisement group'
          text={
            groupUsage[confirmDeleteGroup.id] === 'used'
              ? `The website uses "${confirmDeleteGroup.name}". After deleting it, nothing is shown in that place. Are you sure you want to delete this group?`
              : `Are you sure you want to delete the group "${confirmDeleteGroup.name}"?`
          }
          type='DANGER'
          onCancel={() => setConfirmDeleteGroup(undefined)}
          onConfirm={handleDeleteGroup}
        />
      )}
      <GroupModal
        isOpen={isGroupModalOpen}
        advertisementGroup={editGroup}
        warning={editGroup && groupUsage[editGroup.id] === 'used' ? 'The website finds this group by its exact name. After renaming, its advertisements are no longer shown until the website uses the new name.' : undefined}
        onSaveGroup={handleSaveGroup}
        onClose={closeGroupModal}
        context={context}
      />

      <div className='builder-flex builder-flex-wrap builder-justify-between builder-items-center builder-gap-4 builder-mb-5'>
        <div className='builder-text-2xl builder-text-black builder-font-bold'>Advertisements</div>
        <div className='builder-flex builder-items-center builder-gap-3'>
          {canSyncAdvertisements && (
            <button
              type='button'
              onClick={() => setConfirmSync(true)}
              disabled={syncing}
              className='builder-px-4 builder-py-2 builder-bg-white builder-text-primary builder-border builder-border-primary builder-font-semibold builder-whitespace-nowrap'
              data-testid='advertisements-sync'
            >
              <i className={`fal ${syncing ? 'fa-spinner-third fa-spin' : 'fa-sync'} builder-mr-2`} />
              Sync advertisements
            </button>
          )}
          <button
            type='button'
            onClick={() => openGroupModal(undefined)}
            className='builder-px-4 builder-py-2 builder-bg-white builder-text-primary builder-border builder-border-primary builder-font-semibold builder-whitespace-nowrap'
            data-testid='advertisement-group-new'
          >
            <i className='fal fa-plus builder-mr-2' />
            New group
          </button>
          <button type='button' onClick={onCreateAdvertisement} className='builder-px-4 builder-py-2 builder-bg-primary builder-text-white builder-border builder-border-primary builder-font-semibold builder-whitespace-nowrap'>
            <i className='fal fa-plus builder-mr-2' />
            New advertisement
          </button>
        </div>
      </div>

      {syncResults && (
        <div className='builder-mb-5 builder-p-4 builder-bg-white builder-border builder-border-gray-200 builder-shadow-sm' data-testid='advertisements-sync-results'>
          <div className='builder-flex builder-items-center builder-justify-between builder-mb-2'>
            <span className='builder-font-bold builder-text-black'>Sync results</span>
            <button type='button' onClick={() => setSyncResults(undefined)} aria-label='Close sync results'>
              <i className='fal fa-times' />
            </button>
          </div>
          <ul>
            {map(syncResults, (result) => (
              <li key={result.url} className={`builder-text-sm ${result.ok ? 'builder-text-green-700' : 'builder-text-red-700'}`}>
                <i className={`fal ${result.ok ? 'fa-check' : 'fa-times'} builder-mr-2`} />
                {result.url}
                {result.error && `: ${result.error}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className='builder-flex builder-flex-wrap builder-items-start builder-gap-6'>
        <nav aria-label='Advertisement groups' className='builder-flex-shrink-0 builder-w-80 builder-bg-white builder-border builder-border-gray-200 builder-shadow-sm'>
          <div className='builder-p-3'>
            <Search onChange={(newSearchTerm) => setSearch(newSearchTerm)} value={search} dataTestId='advertisement-groups-search' placeholder='Find a group or advertisement' minWidth={0} />
          </div>
          {renderGroupList('Groups with ads', groupsWithAds, 'advertisement-groups-with-ads')}
          {renderGroupList('Groups without ads', groupsWithoutAds, 'advertisement-groups-without-ads')}
          <div className='builder-border-t-4 builder-border-gray-200 builder-bg-gray-50'>
            <div className='builder-px-4 builder-pt-3 builder-text-sm builder-font-bold builder-uppercase builder-tracking-wider builder-text-gray-600'>Ads without a group</div>
            <Link
              to={`${routes.ADVERTISEMENTS}/${NO_GROUP}`}
              aria-current={selectedKey === NO_GROUP ? 'page' : undefined}
              className={`builder-flex builder-items-center builder-justify-between builder-gap-2 builder-px-4 builder-pt-1 builder-pb-4 builder-border-l-4 ${selectedKey === NO_GROUP ? 'builder-border-primary' : 'builder-border-transparent'}`}
            >
              <span className='builder-flex builder-flex-col'>
                <span className='builder-font-semibold builder-text-black'>{countLabel(advertisementsWithoutGroup.length)}</span>
                <span className='builder-text-sm builder-text-gray-600'>Not shown anywhere until you add them to a group</span>
              </span>
              <i className='fal fa-chevron-right builder-text-gray-600' />
            </Link>
          </div>
        </nav>

        <div className='builder-flex builder-flex-col builder-gap-4' style={{ flex: '999 1 560px', minWidth: 0 }}>
          {selectedGroup ? renderGroupPanel(selectedGroup) : renderNoGroupPanel()}
        </div>
      </div>
    </div>
  );
};

export default Overview;
